/* eslint-disable max-classes-per-file,no-useless-escape */
import React from 'react';
import { ObjectHelpers } from './object.helpers';

export type ValidationResult = { [key: string]: string } | null;
export type ValidatorFunc = (v: string, formState: any) => { [key: string]: string } | null;
export type FormValidators = { [fieldName: string]: ValidatorFunc[] };
export type FormValidationResult = { [fieldName: string]: ValidationResult };
export type CustomFormValidators<T> = { [U in keyof T]?: ValidatorFunc[] };

export class Validators {
    static readonly EMAIL_REG = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    static required(v: string, msg?: string): ValidationResult {
        if (!v) {
            return { required: msg || 'Required' };
        }
        return null;
    }

    static minArrayLength(v: string, minCount: number, msg?: string): ValidationResult {
        if (!v || !Array.isArray(v) || v.length < minCount) {
            return { min: msg || `At least ${minCount} selection${minCount > 1 ? 's' : ''} required` };
        }
        return null;
    }

    static minLength(v: string, minLength: number): ValidationResult {
        if (!v || v.length < minLength) {
            return { min: `Min ${minLength} characters` };
        }
        return null;
    }

    static maxLength(v: string, maxLength: number): ValidationResult {
        if (v && v.length > maxLength) {
            return { max: `Max ${maxLength} characters` };
        }
        return null;
    }

    static isNumber(v: string): ValidationResult {
        const str = v.toString();
        const parsedStr = parseFloat(v).toString();
        if (str !== parsedStr) {
            return { number: 'Must be a number' };
        }
        return null;
    }

    static isNonNegativeNumber(v: string): ValidationResult {
        if (v) {
            const numeric = +v;
            if (Number.isNaN(numeric) || numeric < 0) {
                return { positiveNumber: 'Must be a non-negative number' };
            }
        }
        return null;
    }

    static isPositiveNumber(v: string): ValidationResult {
        if (v) {
            const numeric = +v;
            if (Number.isNaN(numeric) || numeric <= 0) {
                return { positiveNumber: 'Must be a positive number' };
            }
        }
        return null;
    }

    static isLessThanOrEquals(v: string, max: number, msg?: string): ValidationResult {
        if (v) {
            const numeric = +v;
            if (Number.isNaN(numeric) || numeric > max) {
                return { lessThanOrEquals: msg || `Must be ${max} or less` };
            }
        }
        return null;
    }

    static isGreaterThanOrEquals(v: string, min: number, msg?: string): ValidationResult {
        if (v) {
            const numeric = +v;
            if (Number.isNaN(numeric) || numeric < min) {
                return { greaterThanOrEquals: msg || `Must be ${min} or more` };
            }
        }
        return null;
    }

    static regex(v: string, pattern: string, msg: string = ''): ValidationResult {
        if (v) {
            const r = new RegExp(pattern);
            if (!r.test(v)) {
                return { regEx: msg };
            }
        }
        return null;
    }

    static email(v: string, msg?: string): ValidationResult {
        const regExp = new RegExp(this.EMAIL_REG);
        if (!regExp.test(v)) {
            return { email: msg || 'Must be a valid email' };
        }
        return null;
    }

    static url(v: string, msg?: string): ValidationResult {
        if (v && (!v.startsWith('https://') && !v.startsWith('http://'))) {
            return { url: msg || 'Must be a URL' };
        }
        return null;
    }

    static notContainedIn(v: string, list: string[] = [], msg?: string): ValidationResult {
        if (v && (list.some((li) => li === v))) {
            return { notContainedIn: msg || 'Value must not already exist' };
        }
        return null;
    }

    static allowedEmailDomain(v: string, domains: string | null, msg?: string): ValidationResult {
        if (v) {
            if (!domains?.trim()) return null;

            const allowedDomains = domains.split(',').map((d) => d.trim());
            if (allowedDomains.length === 0) return null;

            const emailDomain = v.split('@')[1];
            if (emailDomain && !allowedDomains.includes(emailDomain)) {
                return { email: msg || `Domain not in ${domains}` };
            }
        }
        return null;
    }

    // eslint-disable-next-line no-unused-vars
    static validate: (validators: FormValidators, field: string, value: string, formState: any) => ValidationResult = (validators, field, value, formState) => {
        const fieldValidators = validators[field];
        let result: ValidationResult = {};
        if (fieldValidators) {
            fieldValidators.forEach((v) => {
                const single = v(value, formState);
                if (single) {
                    result = { ...result, ...single };
                }
            });
            if (Object.keys(result).length > 0) {
                const errorSummary: ValidationResult = { errorSummary: Object.values(result || {}).join(', ') };
                result = { ...result, ...errorSummary };
            }
        }
        return Object.keys(result).length > 0 ? result : null;
    };
}

export class FormValidator<T extends object> extends Validators {
    hasErrors: boolean = false;

    private _formModel: T;

    private _setFormModel: (formModel: T) => void;

    private _errors: FormValidationResult;

    private _setErrors: (errors: FormValidationResult) => void;

    private _changedTracker?: (hasChanges: boolean) => void;

    constructor(
        private validators: CustomFormValidators<T>,
        private formState: [T, React.Dispatch<React.SetStateAction<T>>],
        private errorState: [FormValidationResult, React.Dispatch<React.SetStateAction<FormValidationResult>>],
        private changedTracker?: (hasChanges: boolean) => void,
    ) {
        super();
        [this._formModel, this._setFormModel] = formState;
        [this._errors, this._setErrors] = errorState;
        if (changedTracker) {
            this._changedTracker = changedTracker;
        }
    }

    validateAll: () => boolean = () => {
        Object.keys(this._formModel).forEach((field) => {
            const typedField = field as keyof T;
            const value = this._formModel[typedField];
            this.validate(typedField, value, this.formState);
        });

        this.hasErrors = Object.keys(this._errors).length > 0;
        return !this.hasErrors;
    };

    handleFormChange: (event: React.ChangeEvent<HTMLInputElement> | {
        target: { name: string, value: any, checked?: any, type?: string }
    }) => void = (event) => {
        const field = event.target.name as keyof T;

        switch (event.target.type) {
            case 'checkbox': {
                const value = event.target.checked;
                if (Array.isArray(this._formModel[field])) {
                    const checkedValue = event.target.value;
                    const arr = this._formModel[field] as unknown as any[];
                    const newValue = arr.filter((v) => v !== checkedValue);
                    if (value) {
                        newValue.push(checkedValue);
                    }
                    this._formModel[field] = [...newValue] as unknown as T[keyof T];
                } else {
                    this._formModel[field] = value;
                }

                break;
            }
            default:
                this._formModel[field] = event.target.value;
                break;
        }

        this.validate(field, this._formModel[field], this.formState);
        if (this._changedTracker) {
            this._changedTracker(true);
        }
    };

    isValid: () => T | null = () => (this.validateAll() ? this._formModel : null);

    private validate: (field: keyof T, value: string | any, formState: any) => void = (field, value, formState) => {
        const fieldValidators = this.validators[field];
        let result: ValidationResult = {};
        if (fieldValidators) {
            fieldValidators.forEach((v) => {
                const single = v(value, formState);
                if (single) {
                    result = { ...result, ...single };
                }
            });
        }

        const errorSummary: ValidationResult = { errorSummary: Object.values(result || {}).join(', ') };
        const fieldErrors = { [field]: Object.keys(result).length > 0 ? { ...result, ...errorSummary } : null };
        this._errors = ObjectHelpers.removeNullAndUndefined({ ...this._errors, ...fieldErrors });

        this._setErrors(this._errors);
    };
}
